import { useLazyQuery } from '@apollo/client';
import { Col, DatePicker, Form, Input, InputNumber, Row, Select } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';

import Checkbox from 'antd/es/checkbox/Checkbox';
import { AppContext } from '../../../../../AppContext';
import {
  ANNUALLY_LITE,
  PLAN_DURATION_DAYS,
  PLAN_NAMES,
  PLAN_TYPES,
  SORT,
} from '../../../../../common/constants';
import {
  formValidatorRules,
  getPlayPlanLabel,
} from '../../../../../common/utils';
import { SUBSCRIPTION_PLANS_ADMIN_SELECT } from '../../../../subscription/graphql/Queries';

const {
  sellingPrice,
  required,
  email,
  phone,
  sellingPriceRule,
  whitespaceNotAllowed,
  name,
} = formValidatorRules;
const UserPayoutsLinksForm = ({ form, isView, setPlanExpiryDate }) => {
  const [planList, setPlanList] = useState();
  const [isPlansLoading, setIsPlansLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const {
    state: { currentUser },
  } = useContext(AppContext);

  const [subscriptionPlansAdmin] = useLazyQuery(
    SUBSCRIPTION_PLANS_ADMIN_SELECT,
    {
      onCompleted: (response) => {
        const hiddenPlans =
          process.env.REACT_APP_HIDE_PLANS_SLUG?.split(',') || [];
        const filteredData = response?.subscriptionPlansAdmin?.data?.filter(
          (item) => !hiddenPlans?.includes(item?.slug),
        );
        setPlanList(filteredData);
      },
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  useEffect(() => {
    setIsPlansLoading(true);
    subscriptionPlansAdmin({
      variables: {
        filter: {
          planVersion: 'v2',
        },
        sort: {
          field: 'name',
          order: SORT?.ASC,
        },
      },
    });
    setIsPlansLoading(false);
  }, []);

  const planListArray = planList?.map((plan) => ({
    key: plan?.id,
    label: `${
      plan?.name === PLAN_NAMES.PLAY.value
        ? getPlayPlanLabel(plan)
        : PLAN_NAMES[plan?.name]?.key
    }-${PLAN_TYPES[plan?.type]?.key} ${
      plan?.subscriptionFeatures?.planVersion
        ? `(${plan?.subscriptionFeatures?.planVersion})`
        : ''
    } - ₹${plan?.price} ${plan?.slug === ANNUALLY_LITE ? ' (Lite)' : ''}`,
  }));
  const calculateActualPrice = (plan, isDamageWaiver) =>
    isDamageWaiver
      ? plan?.price +
        plan?.subscriptionFeatures?.actualChargesForDamageWaiverFees
      : plan?.price;

  const handleDamageWaiverChange = (e) => {
    const { checked } = e.target;
    if (selectedPlan) {
      const newActualPrice = calculateActualPrice(selectedPlan, checked);

      form.setFieldsValue({
        actualPrice: newActualPrice,
      });
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      disabled={isView}
      className={`payment-links-form ${isView ? 'common-view-form' : 'form'}`}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12} md={12} lg={12} xl={6} xxl={6}>
          <Form.Item
            label="User Phone Number"
            name="contactNo"
            rules={[
              { ...required, message: 'Please Enter Phone Number!' },
              phone,
            ]}
          >
            <Input
              allowClear
              placeholder="Enter User Phone Number"
              maxLength={10}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={12} xl={6} xxl={6}>
          <Form.Item
            label="User Name"
            name="name"
            rules={[name, whitespaceNotAllowed]}
          >
            <Input allowClear placeholder="Enter User Name" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={12} xl={6} xxl={6}>
          <Form.Item
            label="User Email ID"
            name="email"
            rules={[email, whitespaceNotAllowed]}
          >
            <Input allowClear placeholder="Enter User Email" maxLength={255} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={12} xl={6} xxl={6}>
          <Form.Item
            label="Plan"
            name="plan"
            rules={[
              {
                required: true,
                message: 'Please select plan!',
              },
            ]}
          >
            <Select
              loading={isPlansLoading}
              placeholder="Select plan"
              onChange={(value) => {
                const selected = planList.find((plan) => plan.id === value);
                setSelectedPlan(selected);
                setPlanExpiryDate(undefined);
                const damageWaiver =
                  form.getFieldValue('userDamageWaiverFeesApplied') || false;
                const actualPrice = calculateActualPrice(
                  selected,
                  damageWaiver,
                );

                const planDays = PLAN_DURATION_DAYS[selected?.type] || 30;
                const planExpiry = dayjs().add(planDays, 'days');

                form.setFieldsValue({
                  actualPrice,
                  sellingPrice: actualPrice,
                  planExpiryDate: planExpiry,
                });
              }}
            >
              {planListArray?.map((plan) => (
                <Select.Option key={plan.key} value={plan.key}>
                  {plan.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={12} lg={12} xl={6} xxl={6}>
          <Form.Item label="Actual Price" name="actualPrice" disabled>
            <InputNumber
              disabled
              style={{ width: '100%' }}
              placeholder="Enter amount"
              addonBefore="₹"
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={12} lg={12} xl={6} xxl={6}>
          <Form.Item
            label="Selling Price"
            name="sellingPrice"
            rules={[
              sellingPrice,
              {
                required: true,
                message: 'Please enter selling price!',
              },
              // commenting for now, can be used later
              // ...(!currentUser?.roles?.includes(USER_ROLES.SUPER_ADMIN.key) &&
              // selectedPlan?.minimumSellingPrice
              //   ? [sellingPriceRule(selectedPlan?.minimumSellingPrice)]
              //   : []),
            ]}
            dependencies={['actualPrice']}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter amount"
              addonBefore="₹"
              parser={(value) => value.replace(/₹\s?|(,*)/g, '')}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={12} lg={12} xl={6} xxl={6}>
          <Form.Item
            label="Payout Link Expiry Date & Time"
            name="expiredAt"
            rules={[
              {
                required: true,
                message: 'Please select expiry date!',
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  const nowPlus15 = dayjs().add(15, 'minute');
                  if (value.isBefore(nowPlus15)) {
                    // eslint-disable-next-line prefer-promise-reject-errors
                    return Promise.reject(
                      'Expiry time must be at least 15 minutes from now',
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
            initialValue={dayjs().add(1, 'day')}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              disabledDate={(current) =>
                current && current < dayjs().startOf('day')
              }
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12} md={12} lg={12} xl={6} xxl={6}>
          <Form.Item
            label="Plan Expiry Date"
            name="planExpiryDate"
            rules={[
              {
                required: true,
                message: 'Please select expiry date!',
              },
              {
                validator: (_, value) => {
                  if (!value || !selectedPlan) return Promise.resolve();

                  const planDays = PLAN_DURATION_DAYS[selectedPlan?.type] || 30;
                  const minDate = dayjs().add(planDays, 'days');
                  const maxDate = minDate.add(1, 'month');

                  if (value.isBefore(minDate.startOf('day'))) {
                    // eslint-disable-next-line prefer-promise-reject-errors
                    return Promise.reject(
                      `Plan expiry date must be on or after ${minDate.format(
                        'DD/MM/YYYY',
                      )}`,
                    );
                  }

                  if (value.isAfter(maxDate.endOf('day'))) {
                    // eslint-disable-next-line prefer-promise-reject-errors
                    return Promise.reject(
                      `Plan expiry date must be within one month from ${minDate.format(
                        'DD/MM/YYYY',
                      )}`,
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
            dependencies={['plan']}
            initialValue={
              selectedPlan
                ? dayjs().add(
                    PLAN_DURATION_DAYS[selectedPlan?.type] || 30,
                    'days',
                  )
                : null
            }
          >
            <DatePicker
              popupClassName="plan-expiry-datepicker"
              className="plan-expiry-datepicker"
              style={{ width: '100%' }}
              disabledDate={(current) => {
                if (!current || !selectedPlan) return false;

                const planDays = PLAN_DURATION_DAYS[selectedPlan?.type] || 30;
                const minDate = dayjs().add(planDays, 'days');
                const maxDate = minDate.add(1, 'month');

                return (
                  current.isBefore(minDate.startOf('day')) ||
                  current.isAfter(maxDate.endOf('day'))
                );
              }}
              disabled={!selectedPlan}
              onChange={(date) => {
                if (date) {
                  setPlanExpiryDate?.(date);
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={12} xl={6} xxl={6}>
          <Form.Item
            name="userDamageWaiverFeesApplied"
            valuePropName="checked"
            label="Damage Waiver"
          >
            <Checkbox
              onChange={handleDamageWaiverChange}
              disabled={
                !selectedPlan?.subscriptionFeatures?.damageWaiverFeesApplied ||
                !selectedPlan?.subscriptionFeatures
                  ?.actualChargesForDamageWaiverFees
              }
            >
              Include Damage Waiver
              {selectedPlan?.subscriptionFeatures
                ?.actualChargesForDamageWaiverFees &&
                `(+₹${selectedPlan?.subscriptionFeatures?.actualChargesForDamageWaiverFees}
              )`}
            </Checkbox>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={24} xl={12} xxl={12}>
          <Form.Item label="Notes" name="notes" rules={[whitespaceNotAllowed]}>
            <TextArea
              rows={1}
              allowClear
              placeholder="Enter Notes"
              showCount
              maxLength={700}
              cols={3}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default UserPayoutsLinksForm;
